import { computed, ComputedRef } from "vue";
import { useStore } from "vuex";
import { IOptionDropdown } from "@/types/dropdown";
import { LanguageLocale } from "@/types/language";
import { MerchantCountry } from "@/types/merchant";
import { getLanguage } from "@/apps/merchant3/shared-helpers/language.helper";

type ComposableResult = {
  customerLocaleCode: ComputedRef<string>;
  customerLocaleName: ComputedRef<string>;
  customerDefaultLanguage: ComputedRef<{ name: string; code: string }>;
  setCustomerLocale: (value: IOptionDropdown, callback?: () => void) => void;
};

export function useCustomerLocale(): ComposableResult {
  const store = useStore();
  const merchantPreferredLanguage = computed(
    () => store.state.merchant?.merchant?.languagePreference
  );
  const merchantCountry = computed(
    () => store.state.merchant?.merchant?.country
  );

  const customerLocaleCode = computed(() => {
    let defaultLanguage = merchantPreferredLanguage.value || getLanguage();

    if (!defaultLanguage) {
      switch (merchantCountry.value) {
        case MerchantCountry.ZA:
          defaultLanguage = LanguageLocale.ZA;
          break;
        case MerchantCountry.CO:
          defaultLanguage = LanguageLocale.CO;
          break;
        case MerchantCountry.MX:
          defaultLanguage = LanguageLocale.MX;
          break;
        case MerchantCountry.BR:
          defaultLanguage = LanguageLocale.BR;
          break;
      }
    }

    return sessionStorage.getItem("customerLocaleCode") || defaultLanguage;
  });

  const customerLocaleName = computed(
    () => sessionStorage.getItem("customerLocaleName") || "English"
  );

  const customerDefaultLanguage = computed(() => ({
    name: customerLocaleName.value,
    code: customerLocaleCode.value,
  }));

  const setCustomerLocale = (
    value: IOptionDropdown,
    callback?: () => void
  ): void => {
    sessionStorage.setItem("customerLocaleCode", value.code);
    sessionStorage.setItem("customerLocaleName", value.name);
    callback && callback();
  };

  return {
    customerLocaleCode,
    customerLocaleName,
    customerDefaultLanguage,
    setCustomerLocale,
  };
}
